/**
 * @module  js/services/viewtoggleservice.js
 * @class   LoadingIndicatorService
 * @summary Manages page-wide loading spinners so only one is visible at a time.
 *
 * @description
 * Provides a simple registry that maps logical spinner IDs to DOM elements.
 * Guarantees clean UX by automatically hiding any previously visible spinner before showing a new one.
 *
 * ## Key Features
 * – Central registry of spinner elements keyed by ID.
 * – Auto-hides the currently active spinner when another is shown.
 * – Optional message text injection for context-specific feedback.
 * – Lightweight with no external dependencies.
 *
 * ## Public API
 * – `register(id, el)` — boot-time hookup of a spinner element
 * – `show(id, msg)` — display the spinner with optional text
 * – `hide(id)` — hide the specified spinner if visible
 * – `isVisible(id)` — boolean status helper
 *
 * ## Dependencies
 * (none)
 *
 * @author   Auto-Generated
 * @version  1.0.0
 * @since    2025-04-28
 */

const DEFAULT_LOADING_INDICATOR_STYLE = 'ship';
const LOADING_INDICATOR_STYLE_CLASSIC = 'classic';
const LOADING_INDICATOR_STYLE_STORAGE_KEY = 'topsail.loadingIndicatorStyle';

/**
 * Normalize a loading indicator style into the supported runtime set.
 *
 * @param {unknown} value - Raw style value from storage, Firestore, or UI.
 * @returns {'ship'|'classic'} Supported loading indicator style.
 */
function normalizeLoadingIndicatorStyle(value) {
  return String(value || '')
    .trim()
    .toLowerCase() === LOADING_INDICATOR_STYLE_CLASSIC
    ? LOADING_INDICATOR_STYLE_CLASSIC
    : DEFAULT_LOADING_INDICATOR_STYLE;
}

/**
 * Read the cached loading indicator preference without requiring auth.
 *
 * @param {{ getItem?: (key: string) => string|null }|null|undefined} storage - Storage adapter.
 * @returns {'ship'|'classic'} Cached loading indicator style.
 */
function readStoredLoadingIndicatorStyle(storage) {
  try {
    return normalizeLoadingIndicatorStyle(
      storage?.getItem?.(LOADING_INDICATOR_STYLE_STORAGE_KEY)
    );
  } catch {
    return DEFAULT_LOADING_INDICATOR_STYLE;
  }
}

export default class LoadingIndicatorService {
  #map = new Map(); // id → HTMLElement
  #current = null;
  #timers = new Map(); // id → timeout handle for delayed show
  #document = null;
  #style = DEFAULT_LOADING_INDICATOR_STYLE;

  /**
   * Create a loading indicator registry.
   *
   * @param {{ document?: Document, storage?: Storage|{ getItem?: Function } }} [options={}] - Runtime adapters.
   */
  constructor({
    document = globalThis?.document,
    storage = globalThis?.localStorage,
  } = {}) {
    this.#document = document || null;
    this.#style = readStoredLoadingIndicatorStyle(storage);
    this.#applyStyleToDocument();
  }

  /**
   * Apply a loading indicator style to the page and registered images.
   *
   * @param {unknown} style - Raw style value.
   * @returns {'ship'|'classic'} Applied loading indicator style.
   */
  setStyle(style) {
    this.#style = normalizeLoadingIndicatorStyle(style);
    this.#applyStyleToDocument();
    return this.#style;
  }

  /**
   * Resolve the active document for DOM mutations.
   *
   * @returns {Document|null} Active document, when available.
   */
  #getDocument() {
    return this.#document || globalThis?.document || null;
  }

  /**
   * Toggle page-level style state and refresh the currently visible loader.
   *
   * @returns {void}
   */
  #applyStyleToDocument() {
    const doc = this.#getDocument();
    if (!doc?.documentElement) return;

    doc.documentElement.dataset.loadingIndicatorStyle = this.#style;
    const currentEl = this.#current ? this.#map.get(this.#current) : null;
    if (currentEl) this.#hydrateIndicatorImages(currentEl);
  }

  /**
   * Set or remove a loader image `src` based on the active style.
   *
   * @param {HTMLImageElement|Element} image - Loader image element.
   * @returns {void}
   */
  #hydrateIndicatorImage(image) {
    const src = image.getAttribute('data-loading-indicator-src');
    if (!src) return;

    if (this.#style === DEFAULT_LOADING_INDICATOR_STYLE) {
      image.setAttribute('src', src);
      image.hidden = false;
      image.removeAttribute('data-loading-indicator-hidden');
      return;
    }

    image.removeAttribute('src');
    image.hidden = true;
    image.setAttribute('data-loading-indicator-hidden', 'true');
  }

  /**
   * Hydrate all deferred loader images inside a visible spinner.
   *
   * @param {Element|null} root - Spinner element to scan.
   * @returns {void}
   */
  #hydrateIndicatorImages(root) {
    if (!root?.querySelectorAll) return;
    root
      .querySelectorAll('img[data-loading-indicator-src]')
      .forEach((image) => {
        this.#hydrateIndicatorImage(image);
      });
  }

  register(id, el) {
    if (!id || !el)
      throw new Error('LoadingIndicatorService.register: bad args');
    el.classList.add('hidden');
    el.dataset.loadingIndicatorStyle = this.#style;
    this.#map.set(id, el);
    this.#applyStyleToDocument();
  }

  show(id, msg = 'Loading…') {
    const el = this.#map.get(id);
    if (!el) throw new Error(`Spinner '${id}' not registered`);

    // hide any other spinner
    if (this.#current && this.#current !== id) {
      this.hide(this.#current);
    }

    // only set text if the element exists
    const textEl = el.querySelector('.spinner-text');
    if (textEl) {
      textEl.textContent = msg;
    }

    this.#hydrateIndicatorImages(el);
    el.classList.remove('hidden');
    this.#current = id;
  }

  /** Show the spinner only if `delay` ms elapse before hide() is called. */
  showAfterDelay(id, delay = 300, msg = 'Loading…') {
    if (this.#timers.has(id) || this.isVisible(id)) return;
    const handle = setTimeout(() => {
      this.#timers.delete(id);
      this.show(id, msg);
    }, delay);
    this.#timers.set(id, handle);
  }

  hide(id) {
    const t = this.#timers.get(id);
    if (t) {
      clearTimeout(t);
      this.#timers.delete(id);
    }
    const el = this.#map.get(id);
    if (!el) return;
    el.classList.add('hidden');
    if (this.#current === id) this.#current = null;
  }

  isVisible(id) {
    return this.#current === id;
  }
}
